/*
  a group of reusable renderers for common columns in tables of alarms
  these renderers are currently set up to support a mixture of v1 and v2 alarms
*/
import React from 'react';
import moment from 'moment';
import { dateTimeFormatter } from 'core/util/dateUtils';
import { Box, ButtonLink, CopyText, Flex, Grid, Icon, Link, Tag, Text, Tooltip } from 'core/components';
import { CONDITION_TYPE_LABELS } from '@kentik/ui-shared/synthetics/bgp';
import { isAlarmActive, isEqualStrings, getFirstTriggerTime, toLabel } from '@kentik/ui-shared/synthetics/utils';
import {
  ALARM_NULL_END_DATE,
  ALERT_STATE_LABELS,
  ALERT_UI_STATES,
  ALERT_STATE_API_TO_UI,
  SEVERITY_MAP
} from '@kentik/ui-shared/alerting/constants';
import { Label } from 'app/components/labels/Label';
import SiteTag from 'app/components/site/SiteTag';
import { getTestTypeLabel, healthToIntent } from 'app/views/synthetics/utils/syntheticsUtils';
import TestErrorPopover from 'app/views/synthetics/TestErrorPopover';
import MetricThresholdPopover from 'app/views/synthetics/components/MetricThresholdPopover';
import AgentRenderer from 'app/views/synthetics/components/AgentRenderer';
import SeverityIcon from 'app/views/synthetics/components/incidentLogs/SeverityIcon';

export function getTestResultsLink({
  test,
  agentId,
  targetAgentId,
  target,
  startTime,
  endTime,
  dateRangeOnly = false
}) {
  let testUrl;
  let display;

  if (test) {
    testUrl = `/v4/synthetics/tests/${test.id}/results`;
    display = test.get('display_name');

    if (agentId && !dateRangeOnly) {
      testUrl += `/agent/${agentId}`;

      if (target && !test.isPageLoad && !test.isTransaction) {
        // Mesh tests and A2A tests now use the target_agent_id in the url param for subtest page
        if (test.isMesh || test.isAgentTest) {
          testUrl += `/${targetAgentId}`;
        } else if (!test.isHostname) {
          // hostname tests don't have target in the subtest url anymore
          testUrl += `/${target}`;
        }
      }
    }

    // the test results page state subtracts 15 minutes from this param
    const start = moment.utc(startTime).startOf('minute').unix(); // drop down to the nearest whole minute
    const rawEnd = endTime;
    const thirtyMinutesFromStart = moment.utc(startTime).add(30, 'minutes').unix();
    let end = null;

    if (!rawEnd || rawEnd === ALARM_NULL_END_DATE) {
      // if the end is undefined in either v1 or v2 terms (alarm is active), use the calculated 30 mins from start time value
      // but make sure to take the lower of that value and the current time to prevent populating a date picker with an end date in the future
      end = Math.min(moment.utc().unix(), thirtyMinutesFromStart);
    } else {
      // otherwise, take the lower of the 30 mins from start or end time
      // the test results page state adds 15 minutes to this param
      end = Math.min(
        moment.utc(endTime).unix(),
        // we want it to be no more than 1 hour of data so we get full granularity
        // so cap it at +30 minutes since the page will automatically add another 15 minutes on both sides.
        thirtyMinutesFromStart
      );
    }

    if (dateRangeOnly && start === test.results.startDate && end === test.results.endDate) {
      // don't show a link when we already have this date range selected in test results
      return { testUrl: null, display, title: 'Date range is currently selected' };
    }

    return { testUrl: `${testUrl}?start=${start}&end=${end}`, display };
  }

  return { testUrl, display };
}

export function renderTestResultsLink({ $syn, model, value, startTime, dateRangeOnly = false, raw = false }) {
  const { testId, agentId, targetAgentId } = model.get?.('key.value') || model.key?.value || {};
  const test = testId && $syn.getTest(testId);
  const { target, resolver } = model.get?.('details') || model.details || {};

  const { testUrl, title } = getTestResultsLink({
    test,
    agentId,
    targetAgentId,
    target: test.isDns ? resolver : target,
    startTime,
    endTime: model.get?.('end_time') || model.end_time,
    dateRangeOnly
  });

  if (raw) {
    return testUrl;
  }
  return (
    <CopyText text={value} textProps={{ ellipsis: true }}>
      {testUrl ? (
        <Link to={testUrl} fontWeight="bold">
          {value}
        </Link>
      ) : (
        <Text title={title}>{value}</Text>
      )}
    </CopyText>
  );
}

export function alarmStateRenderer({ raw = false } = {}) {
  return ({ value }) => {
    const state = ALERT_STATE_API_TO_UI[value];
    const label = ALERT_STATE_LABELS[state];

    if (raw) {
      return label;
    }

    return (
      <Tag as="div" intent={state === ALERT_UI_STATES.ACTIVE ? 'danger' : 'success'} width={80} textAlign="center">
        {label}
      </Tag>
    );
  };
}

export function agentRenderer({ $syn, agentId, testId, ...props }) {
  const test = $syn.getTest(testId);

  if (agentId) {
    const agent = $syn.agents.get(agentId);

    if (agent) {
      return <AgentRenderer agent={agent} showLocationInfo {...props} />;
    }
  }

  return (
    <Text fontStyle="italic" muted>
      N/A
      {test && test.isPerTestAlerting && (
        <Tooltip
          content={
            <Text as="div" width={180}>
              Test-based alert settings do not track agent-based data
            </Text>
          }
          minimal
        >
          <Icon ml={1} icon="info-sign" color="primary" />
        </Tooltip>
      )}
    </Text>
  );
}

function endTimeRenderer({ model }) {
  const value = model.get('endTime') || model.get('end_time');
  const state = model.get('state');
  const isAlarm = isAlarmActive(state);
  return isAlarm ? <Text muted>N/A</Text> : dateTimeFormatter({ value });
}

export function durationRenderer({ model }) {
  const state = model.get('state');
  const isAlarm = state === 'alarm' || ALERT_STATE_API_TO_UI[state] === 'alarm';
  const firstTriggerTime = getFirstTriggerTime(model.get('event_start_time'));
  const start = dateTimeFormatter({ value: firstTriggerTime || model.get('start_time') });
  const end = isAlarm ? 'now' : dateTimeFormatter({ value: model.get('end_time') });

  return `${start} to ${end}`;
}

export function severityRenderer({ minimal = false, raw = false } = {}) {
  return ({ value }) => {
    let intent = 'warning';
    let label = 'warning';

    if (value === SEVERITY_MAP.CRITICAL.ALERT_MANAGER || value === SEVERITY_MAP.CRITICAL.STANDARD_ALERT) {
      intent = 'danger';
      label = 'critical';
    }

    if (raw) {
      return label;
    }

    if (minimal) {
      return <SeverityIcon severity={label} />;
    }

    return (
      <Tag
        as="div"
        fontWeight="medium"
        textTransform="capitalize"
        width={80}
        textAlign="center"
        intent={intent}
        minimal
      >
        {label}
      </Tag>
    );
  };
}

export function metricsRenderer(metrics, healthSettings) {
  if (metrics) {
    const description = metrics
      .map(({ metric, value: metricValue }) => `${toLabel(metric)} ${metricValue || ''}`)
      .join('\n');
    return (
      <Flex gap="4px" flexWrap="wrap" title={description}>
        {metrics.map(({ metric, value: metricValue, severity }) => {
          const tag = (
            <Tag key={metric} fontWeight="bold" intent={healthToIntent({ health: severity })} minimal>
              <Flex alignItems="center" gap="4px">
                {/* metrics named 'error' are assumed to be task errors */}
                {isEqualStrings(metric, 'error') && metricValue ? (
                  <>
                    <TestErrorPopover errors={[{ code: metric, message: metricValue }]} />
                    <Text>{toLabel(metric)}</Text>
                  </>
                ) : (
                  <Text>
                    {toLabel(metric)}
                    {metricValue && `: ${metricValue}`}
                  </Text>
                )}
              </Flex>
            </Tag>
          );

          if (healthSettings) {
            return (
              <MetricThresholdPopover
                key={metric}
                metric={metric}
                metricValue={metricValue}
                healthSettings={healthSettings}
              >
                {tag}
              </MetricThresholdPopover>
            );
          }

          return tag;
        })}
      </Flex>
    );
  }

  return '-';
}

export function destinationRenderer({ raw = false } = {}) {
  return ({ value, model }) => {
    const { $syn } = model.store;
    const { testId } = model.get('key.value') || {};
    const testModel = $syn.getTest(testId);

    // check for multiple targets
    const groupData = model.get('details.groupData', {});
    if (groupData.targets?.length > 1) {
      return groupData.targetsGroupLabel;
    }

    let destinationValue = value;

    if (testModel?.isDns) {
      // the destination for dns tests will always prefer showing the resolver
      destinationValue = model.get('details.resolver') || value;
    }

    const targetAgentId = model.get('key.value.targetAgentId');

    if (targetAgentId) {
      if (raw) {
        return $syn.agents.get(targetAgentId)?.label || '--';
      }

      return agentRenderer({ $syn, agentId: targetAgentId });
    }

    return destinationValue || '--';
  };
}

export function testTypeRenderer({ model }) {
  const { $syn } = model.store;
  const { testId } = model.get('key.value') || {};
  const test = $syn.getTest(testId);

  if (!test) {
    return '-';
  }

  return getTestTypeLabel(test.get('test_type'));
}

export function groupedSourceSummaryRenderer({ $syn, groupData, raw = false, showGroups = false } = {}) {
  const { $labels, $sites } = $syn.store;

  if (groupData) {
    const [labelId] = groupData.labelIds || [];
    const [siteId] = groupData.siteIds || [];
    const agentsGroupLabel = groupData.agentsGroupLabel || '--';

    // default to the agent breakdown
    const content = (
      <Flex gap="4px">
        <Icon mt="2px" iconSize={10} icon="grid-view" color="primary" /> <Text>{agentsGroupLabel}</Text>
      </Flex>
    );

    if (Number.isFinite(labelId)) {
      const labelModel = $labels.labels.get(labelId);

      if (raw) {
        return `${labelModel?.get('name') || 'Unassigned'} ${agentsGroupLabel}`;
      }

      if (showGroups) {
        return labelModel ? <Label label={labelModel} /> : <Label name="Unassigned" bg="muted" />;
      }
    }

    if (Number.isFinite(siteId)) {
      const siteModel = $sites.collection.get(siteId);

      if (raw) {
        return `${siteModel?.get('title') || 'Global'} ${agentsGroupLabel}`;
      }

      if (showGroups) {
        return <SiteTag siteName={siteModel?.get('title') || 'Global'} />;
      }
    }

    if (raw) {
      return agentsGroupLabel;
    }

    // fall back to showing a breakdown by agents
    return content;
  }

  return null;
}

function makeCollectionConfig({
  $syn,
  minimal = false,
  testModel = null,
  testResults = false,
  bgp = false,
  setPrefixes
} = {}) {
  return {
    columns: {
      alarm: {
        label: 'Alert ID',
        name: 'id',
        width: 300,
        renderer: ({ value }) => (
          <CopyText text={value} textProps={{ ellipsis: true }}>
            <Link to={`/v4/synthetics/alerts/${value}?force=true`} fontWeight="bold">
              {value}
            </Link>
          </CopyText>
        )
      },

      bgpDisplay: {
        label: 'Type',
        name: 'details.bgp.display',
        width: 150,
        renderer: ({ model, value }) => {
          const subtest = model.get('key.value.subtest');

          if (subtest) {
            return CONDITION_TYPE_LABELS[subtest] || 'Unknown';
          }

          return (
            <Box>
              <Text as="div">{value}</Text>
            </Box>
          );
        }
      },

      bgpPrefix: {
        label: 'Prefix',
        name: 'details.bgp.prefix',
        width: 200,
        renderer: ({ value }) => {
          if (!value || !setPrefixes) {
            return '-';
          }

          return (
            <Box>
              <ButtonLink intent="primary" onClick={() => setPrefixes(value)} minimal small>
                {value}
              </ButtonLink>
            </Box>
          );
        }
      },

      bgpReason: {
        label: 'Details',
        name: 'details.bgp.reason',
        renderer: ({ value }) => {
          if (!value) {
            return '-';
          }

          return (
            <Box>
              <Text as="div">{value}</Text>
            </Box>
          );
        }
      },

      destination: {
        name: 'details.target',
        label: 'Destination',
        renderer: destinationRenderer()
      },

      details: {
        name: 'details',
        label: 'Details',
        renderer: ({ value }) => {
          const { metrics } = value || {};
          const healthSettings = testModel?.get('config.healthSettings');

          return metricsRenderer(metrics, healthSettings);
        }
      },

      duration: {
        label: 'Duration',
        name: 'duration',
        wrapText: true,
        renderer: durationRenderer
      },

      end: {
        label: 'End',
        name: 'end_time',
        width: 150,
        renderer: endTimeRenderer
      },

      severity: {
        label: 'Severity',
        name: 'severity',
        width: minimal ? 50 : 100,
        renderer: severityRenderer({ minimal })
      },

      source: {
        name: 'source',
        label: 'Source',
        minWidth: 180,
        ellipsis: false,
        renderer: ({ model }) => {
          const testId = model.get('key.value.testId');
          const test = $syn.getTest(testId);

          if (test) {
            if (test.isPerAgentAlerting) {
              return agentRenderer({ $syn, agentId: model.get('key.value.agentId'), testId });
            }

            if (test.isGroupedAlerting) {
              return groupedSourceSummaryRenderer({
                $syn,
                groupData: model.get('details.groupData'),
                showGroups: true
              });
            }

            return model.get('details.groupData.agentsGroupLabel', '--');
          }

          return '--';
        }
      },

      start: {
        label: 'Start',
        name: 'start_time',
        width: 150,
        renderer: ({ model, value }) => {
          const dateValue = dateTimeFormatter({ value });

          if (testResults) {
            // test results treatments, offer a link to change only the date range
            return renderTestResultsLink({
              $syn,
              model,
              value: dateValue,
              startTime: value,
              dateRangeOnly: true
            });
          }

          return dateValue;
        }
      },

      firstTrigger: {
        label: 'First Trigger',
        name: 'event_start_time',
        width: 150,
        renderer: ({ model, value }) => {
          const startTime = getFirstTriggerTime(value);

          if (startTime) {
            const dateValue = dateTimeFormatter({ value: startTime });

            if (testResults) {
              // test results treatments, offer a link to change only the date range
              return renderTestResultsLink({
                $syn,
                model,
                value: dateValue,
                startTime,
                dateRangeOnly: true
              });
            }

            return dateValue;
          }

          // legacy v1 alarms and v2 alarms before the 12/24 first trigger release get the N/A treatment
          return 'N/A';
        }
      },

      state: {
        label: 'State',
        name: 'state',
        width: minimal ? 50 : 80,
        renderer: alarmStateRenderer()
      },

      test: {
        name: 'test',
        label: 'Test',
        wrapText: true,
        renderer: ({ model }) => {
          const { testId, agentId, targetAgentId } = model.get('key.value') || {};
          const { target, resolver } = model.get('details') || {};

          if (!testId) {
            return '-';
          }

          const test = $syn.getTest(testId);

          if (!test) {
            return testId;
          }

          const { testUrl, display } = getTestResultsLink({
            test,
            agentId,
            targetAgentId,
            target: test.isDns ? resolver : target,
            startTime: model.get('start_time'),
            endTime: model.get('end_time')
          });

          // supports bgp detailed info below
          const type = model.get('type');
          const details = model.get('details');
          const subtest = model.get('key.value.subtest');
          let errorContent = null;

          if (!!details && (type === 'bgp' || subtest)) {
            // v1 error content
            errorContent = (
              <Grid gridTemplateColumns="50px 1fr" gridGap={1}>
                <Text fontWeight="bold">Type:</Text>
                <Text>{details?.bgp?.display}</Text>
                <Text fontWeight="bold">Prefix:</Text>
                <Text>{details?.bgp?.prefix || '-'}</Text>
                <Text fontWeight="bold">Details:</Text>
                <Text>{details?.bgp?.reason}</Text>
              </Grid>
            );
          }

          return (
            <Flex gap={1}>
              <Link to={testUrl} fontWeight="bold">
                {display}
              </Link>
              {errorContent && <TestErrorPopover content={errorContent} />}
            </Flex>
          );
        }
      },

      testType: {
        name: 'testType',
        label: 'Test Type',
        wrapText: true,
        renderer: testTypeRenderer
      }
    },

    utils: {
      groupByOptions: [
        { label: 'None', value: '' },
        { label: 'Severity', value: 'severity' },
        { label: 'State', value: 'state' }
      ].concat(
        testModel?.isPerAgentAlerting && testResults
          ? [
              { label: 'Agent', value: 'key.value.agentId' },
              { label: 'Target', value: 'details.target' }
            ]
          : [],

        bgp ? { label: 'Prefix', value: 'details.bgp.prefix' } : []
      ),

      groupSummaryLookup: ({ groupKey, group, groupBy }) => {
        // get a model from the group
        const model = group?.[0];
        const hasGroupKey = groupKey !== 'undefined'; // comes in as a string
        let renderedGroup = <Text>{hasGroupKey ? groupKey : '-'}</Text>;
        const renderedLength = <Tag>Alarms: {group.length}</Tag>;

        switch (groupBy) {
          case 'key.value.agentId':
            renderedGroup = agentRenderer({
              $syn,
              agentId: model.get('key.value.agentId'),
              testId: model.get('key.value.testId')
            });
            break;
          case 'details.target': {
            const targetAgentId = model.get('key.value.targetAgentId');

            if (targetAgentId) {
              renderedGroup = agentRenderer({ $syn, agentId: targetAgentId });
            }

            break;
          }
          case 'severity':
            renderedGroup = severityRenderer({ minimal })({ model, value: model.get(groupBy) });
            break;
          case 'state':
            renderedGroup = alarmStateRenderer()({ value: model.get(groupBy) });
            break;
          default:
        }

        return (
          <Flex alignItems="center" gap={1}>
            {renderedGroup}
            {renderedLength}
          </Flex>
        );
      }
    }
  };
}

export function getCollectionConfig({
  $syn,
  minimal = false,
  testModel = null,
  testResults = false,
  bgp = false,
  setPrefixes
} = {}) {
  const {
    columns: {
      alarm,
      bgpDisplay,
      bgpPrefix,
      bgpReason,
      destination,
      details,
      duration,
      end,
      firstTrigger,
      severity,
      source,
      start,
      state,
      test,
      testType
    },
    utils
  } = makeCollectionConfig({ $syn, minimal, testModel, testResults, bgp, setPrefixes });

  if (minimal) {
    // a compact set of columns, currently used in the smaller agent details "Alerts" tab view when in sidebar mode
    return { utils, columns: [severity, state, test, duration] };
  }

  if (bgp) {
    // return bgp-specific alarm columns
    return { utils, columns: [severity, state, bgpDisplay, bgpPrefix, bgpReason, firstTrigger, start, end] };
  }

  if (testResults) {
    // columns for incident log panel in test/agent results
    return { utils, columns: [severity, state, alarm, source, destination, details, firstTrigger, start, end] };
  }

  // the detault set used for main incident log
  return { utils, columns: [severity, state, alarm, test, source, destination, testType, firstTrigger, start, end] };
}
